// Nurse tools, V6 design (README "Nurse tools (2026-10-12)"): Med pass by scanning and the
// controlled-drug count, for the console Worklist and the phone To do. Back end:
// src/aithora/v3/worklist/med-tools.ts and med-scan-rules.ts. The dose itself is still
// recorded by POST /med-pass (worklist-routes.ts medPassRecord) with the scan's id.
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

// ── Med pass by scanning ────────────────────────────────────────────────────────────

export type RightKey = 'resident' | 'medicine' | 'dose' | 'route' | 'time';
export interface RightCheck { key: RightKey; label: string; ok: boolean; text: string }
export type HoldVital = 'pulse' | 'sbp' | 'dbp' | 'temp' | 'spo2';
export type HoldComparator = '<' | '<=' | '>' | '>=';
export interface HoldRule { id: string; drug: string; medicationId: string | null; vital: HoldVital; comparator: HoldComparator; threshold: number; label: string; sample: boolean }
export interface HoldHit { ruleId: string; vital: HoldVital; value: number; comparator: HoldComparator; threshold: number; sample: boolean; label: string; text: string }

export interface MedScanBody {
  /** The order being given. */
  medicationId: string;
  /** The text the wristband scans as (not sent with a skipReason). */
  wristbandCode?: string;
  /** The text the package label scans as (not sent with a skipReason). */
  packageCode?: string;
  /** Strict mode only: give without a scan, for one of the building's reasons (MedPassPolicy.scanSkipReasons). */
  skipReason?: string;
  /** Readings taken now; a hold rule falls back to one charted in the last hour. */
  vitals?: Partial<Record<HoldVital, number>>;
  /** A late or early reason, which lets a dose outside its window go ahead. */
  timingReason?: string;
}

/**
 * POST /med-pass/scan. 200: outcome 'pass' (record it Given with scanId), 'skipped' (strict
 * mode, given without a scan for a listed reason: record it Given with scanId) or 'hold'
 * (record it Held). 422: outcome 'blocked' (a right failed; `reason` says why, nothing was
 * recorded) or 'needs' (take the reading in `needs` first). 409 when scanning is off.
 */
export interface MedScanResult {
  real: true;
  scanId: string;
  outcome: 'pass' | 'blocked' | 'hold' | 'needs' | 'skipped';
  /** The reason picked, on a skipped scan. */
  skipReason?: string;
  ok: boolean;
  failed: RightKey | null;
  reason: string | null;
  rights: RightCheck[];
  holds: HoldHit[];
  needs: HoldVital[];
  resident: { id: string; name: string | null };
  order: { medicationId: string; name: string; dose: string | null; route: string | null };
  recordAs: { status: 'GIVEN' | 'HELD'; reason?: string; scanId: string } | null;
  validUntil: string;
}
export const medPassScan = route<MedScanResult, void, MedScanBody>()('POST', `${A}/med-pass/scan`, { honest: true, acceptStatuses: [422] });

/** PUT /residents/:id/wristband {code}: Facility Admin; null clears it. 409 when another resident has it. */
export const residentWristband = route<{ real: true; residentId: string; wristbandSet: boolean }, void, { code: string | null }>()(
  'PUT', `${A}/residents/:id/wristband`, { honest: true });

export interface ResidentWristband { residentId: string; name: string | null; code: string | null }
/** GET /med-pass/wristbands: each resident of the building and the code on their band (null when none). A PHI read. */
export const medWristbands = route<{ real: true; residents: ResidentWristband[] }, { facility?: string }>()('GET', `${A}/med-pass/wristbands`, { honest: true });

/** Where a package code came from: the pharmacy's order message (no feed connected yet) or typed by the Facility Admin. */
export type PackageSource = 'pharmacy' | 'manual';
/** What the code is: the Rx number on the pharmacy label, the NDC on the package, or another label barcode. */
export type PackageCodeType = 'rx' | 'ndc' | 'label';
export interface PharmacyFeed { connected: boolean; text: string }
export interface MedPackage {
  id: string; code: string; drug: string; strength: string; route: string; residentId: string | null; resident: string | null; medicationId: string | null; createdAt: string | null;
  source?: PackageSource; codeType?: PackageCodeType;
}
/** GET /med-pass/packages: the package codes on file in the building, and whether a pharmacy feed is connected. */
export const medPackages = route<{ real: true; packages: MedPackage[]; pharmacyFeed?: PharmacyFeed }, { facility?: string }>()('GET', `${A}/med-pass/packages`, { honest: true });
/**
 * POST /med-pass/packages: Facility Admin puts a package code on file (always source 'manual').
 * With medicationId, the drug, strength and route the body leaves out come from the order.
 */
export const medPackageAdd = route<{ real: true; id: string; source?: PackageSource; codeType?: PackageCodeType }, void, {
  code: string; codeType?: PackageCodeType; drug?: string; strength?: string; route?: string; residentId?: string; medicationId?: string;
}>()('POST', `${A}/med-pass/packages`, { honest: true });
export const medPackageRetire = route<{ real: true; id: string; retired: true }>()('POST', `${A}/med-pass/packages/:id/retire`, { honest: true });

/** GET /med-pass/hold-rules: the rules in force, the SAMPLE one included unless turned off. */
export const medHoldRules = route<{ real: true; sampleHoldRules: boolean; rules: HoldRule[] }, { facility?: string }>()('GET', `${A}/med-pass/hold-rules`, { honest: true });
/** POST /med-pass/hold-rules: Facility Admin adds a rule (medicine, vital, comparator, threshold). */
export const medHoldRuleAdd = route<{ real: true; id: string }, void, { drug: string; vital: HoldVital; comparator: HoldComparator; threshold: number; medicationId?: string; label?: string }>()(
  'POST', `${A}/med-pass/hold-rules`, { honest: true });
export const medHoldRuleRetire = route<{ real: true; id: string; retired: true }>()('POST', `${A}/med-pass/hold-rules/:id/retire`, { honest: true });

/**
 * The building's med pass scanning set-up (Settings > Med pass).
 *   scanning         the wristband and package scan is in use here (off: POST /med-pass/scan refuses)
 *   requireScan      strict mode: a Given needs a scan, or a skip with a reason from scanSkipReasons
 *   scanSkipReasons  the reasons a nurse may pick (empty: no skip at all)
 *   sampleHoldRules  the design's SAMPLE hold rule applies
 */
export interface MedPassPolicy {
  real: true;
  scanning: boolean;
  requireScan: boolean;
  scanSkipReasons: string[];
  sampleHoldRules: boolean;
  pharmacyFeed: PharmacyFeed;
}
/** GET /med-pass/policy: building staff, and the Aithora team for a building they may read. */
export const medPassPolicyGet = route<MedPassPolicy, { facility?: string }>()('GET', `${A}/med-pass/policy`, { honest: true });
/** PUT /med-pass/policy: Facility Admin. Strict mode needs scanning on; each change is in the Settings history (section med_pass). */
export const medPassPolicySet = route<MedPassPolicy, void, { scanning?: boolean; requireScan?: boolean; scanSkipReasons?: string[]; sampleHoldRules?: boolean }>()(
  'PUT', `${A}/med-pass/policy`, { honest: true });

// ── Controlled-drug count ───────────────────────────────────────────────────────────

export interface CountSheetLine { medicationId: string; residentId: string; resident: string | null; name: string; dosage: string | null; unit: string | null; expected: number | null; lastCountAt: string | null }
export type CountStatus = 'awaiting_second' | 'matched' | 'starting_count' | 'discrepancy';
export interface CountLine {
  medicationId: string; residentId: string; resident: string | null; name: string; dosage: string | null; unit: string | null;
  expected: number | null; counted: number; secondCounted: number | null;
  result: 'matches' | 'off' | 'first_count'; text: string; matches: boolean;
}
export interface CountDiscrepancy { id: string; medicationId: string; kind: 'off_from_record' | 'nurses_disagree'; expected: number | null; counted: number; secondCounted: number | null; foundAt: string | null; alerted: number }
export interface ControlledCount {
  id: string; shift: string | null; status: CountStatus;
  countedBy: { userId: string; name: string | null; at: string | null };
  confirmedBy: { userId: string; name: string | null; at: string | null } | null;
  lines: CountLine[];
  discrepancies: CountDiscrepancy[];
}
export interface CountLineBody { medicationId: string; counted: number }

/** GET /controlled-counts/sheet: what to count, and what the record says each should be (null = first count). */
export const controlledCountSheet = route<{ real: true; lines: CountSheetLine[]; note: string }, { facility?: string }>()('GET', `${A}/controlled-counts/sheet`, { honest: true });
/** POST /controlled-counts: the first nurse. 422 lists what is not counted yet. A mismatch alerts at once. */
export const controlledCountSave = route<ControlledCount & { real: true; mismatch: boolean; alerted: number; message: string; replayed?: boolean }, void, { lines: CountLineBody[]; shift?: string; clientId?: string }>()(
  'POST', `${A}/controlled-counts`, { honest: true });
/** POST /controlled-counts/:id/confirm: a different nurse (403 for the same one); a recount covers every line. */
export const controlledCountConfirm = route<ControlledCount & { real: true; mismatch: boolean; alerted: number; message: string }, void, { recount?: CountLineBody[] }>()(
  'POST', `${A}/controlled-counts/:id/confirm`, { honest: true });
export const controlledCounts = route<{ real: true; counts: ControlledCount[] }, { facility?: string; limit?: number }>()('GET', `${A}/controlled-counts`, { honest: true });
export const controlledCount = route<ControlledCount & { real: true; message: string }, { facility?: string }>()('GET', `${A}/controlled-counts/:id`, { honest: true });
/** POST /controlled-counts/receipts: a pharmacy delivery of a controlled medicine. */
export const controlledReceipt = route<{ real: true; id: string; receivedAt: string | null }, void, { medicationId: string; quantity: number; note?: string }>()(
  'POST', `${A}/controlled-counts/receipts`, { honest: true });

export const medToolsApi = {
  medPassScan, residentWristband, medWristbands, medPackages, medPackageAdd, medPackageRetire, medHoldRules, medHoldRuleAdd, medHoldRuleRetire,
  medPassPolicyGet, medPassPolicySet,
  controlledCountSheet, controlledCountSave, controlledCountConfirm, controlledCounts, controlledCount, controlledReceipt,
} as const;
